import type { QuizStep, QuizMatchProduct } from '@/shared/types'

export interface FragranceFacet {
  label: string
  value: string
  icon?: string
}

export interface FragranceProfile {
  archetypeTitle: string
  tagline: string
  narrative: string
  facets: FragranceFacet[]
  chordsList: string[]
  recommendedVibe: string
}

// Translations dictionary for pure profile generation across uk / en / ru
const profileDict = {
  uk: {
    archetypes: {
      fresh_woody: 'Шляхетний урбаніст: свіжість та дерево',
      leather_oriental: 'Харизматичний алхімік: дим, шкіра та спеції',
      floral_fruity: 'Романтична муза: сонячні квіти та фрукти',
      sweet_amber: 'Чуттєвий гурман: теплий оксамит і прянощі',
      musky_fresh: 'Атлас чистоти: шовковий мускус та свіжість',
      woody_oriental: 'Східний аристократ: шляхетний ліс та амбра',
      fresh_citrus: 'Океанічний драйв: цитрусовий бриз та енергія',
      woody: 'Деревний моноліт: витримка, кедр і сандал',
      floral: 'Квіткова симфонія: витончені пелюстки та елегантність',
      fruity: 'Сонячний нектар: яскраві стиглі фрукти та літо',
      sweet_gourmand: 'Десертний шарм: вишукана ваніль і солодощі',
      oriental_spicy: 'Пряний магнетизм: таємничий схід і ладан',
      leather_smoky: 'Темний лицар: вінтажна шкіра і благородний дим',
      musky_clean: 'Кришталевий мускус: дотик чистоти та пудри',
      animalic: 'Первісна пристрасть: сміливий анімалістичний акорд',
      universal: 'Індивідуальна ольфакторна гармонія',
    },
    taglines: {
      fresh_woody: 'Енергійний баланс природної свіжості та міцної деревної основи.',
      leather_oriental: 'Глибокий, магнетичний і загадковий шлейф із сильним характером.',
      floral_fruity: 'Легкий, життєрадісний і спокусливий коктейль жіночності та радості.',
      sweet_amber: 'Огортаючий, затишний і привабливий аромат із ніжним оксамитом.',
      musky_fresh: 'Елегантна легкість і відчуття свіжовипраної білизни на сонці.',
      woody_oriental: 'Багатий і респектабельний характер із теплими смолистими відтінками.',
      fresh_citrus: 'Чистий заряд бадьорості, морського бризу та іскристих цитрусів.',
      woody: 'Стриманий, глибокий та впевнений у собі деревний профіль.',
      floral: 'Витончена класика, сповнена ніжності, весняного саду та елегантності.',
      fruity: 'Яскравий вибух тропічних фруктів, соковитих ягід і оптимізму.',
      sweet_gourmand: 'Апетитний, теплий і розкішний аромат розтопленої карамелі та ванілі.',
      oriental_spicy: 'Загадкова подорож східними прянощами та амбровими сутінками.',
      leather_smoky: 'Сміливий, бунтарський і статусний профіль з тютюном та шкірою.',
      musky_clean: 'Ніжна пудрова хмаринка, що інтимно тримається близько до шкіри.',
      animalic: 'Провокаційний, гіпнотичний та пристрасний темперамент.',
      universal: 'Збалансоване поєднання нот для будь-якої події та настрою.',
    },
    narratives: {
      intro_woody: 'Ваш ольфакторний вибір підкреслює глибоку впевненість, статус і шляхетність.',
      intro_fresh: 'Ваш ароматний портрет дихає свіжістю, рухом, свободою та невимушеною легкістю.',
      intro_floral: 'Ваш вибір розкриває витончений романтизм, чутливість і гармонійну елегантність.',
      intro_gourmand: 'Ваш профіль випромінює затишок, тепло, солодку спокусливість та апетитну чарівність.',
      intro_leather: 'Ваш образ сповнений харизми, сміливості, стриманої сили та вінтажного шику.',
      intro_oriental: 'Ваш аромат огортає східною таємницею, благородними смолами та пряною пристрастю.',
      intro_clean: 'Ваш ольфакторний почерк — це бездоганна чистота, охайність і вишуканий мінімалізм.',
      intro_default: 'Ваш персональний ольфакторний профіль створено на основі унікального поєднання обраних акцентів.',
      
      seasons_warm: 'Така композиція неперевершено сяє у теплу пору року, розкриваючись кришталевими та грайливими переливами під сонячним промінням.',
      seasons_cold: 'Цей аромат ідеально розкривається в прохолодну та морозну пору, огортаючи шляхетним теплом і зігріваючи своїм глибоким серцем.',
      seasons_heat: 'Призначений для спекотних літніх днів: освіжає, тонізує та дарує відчуття абсолютного комфорту.',
      seasons_frost: 'Густа і насичена база створена для осінньо-зимової погоди, залишаючи розкішний оксамитовий шлейф на шарфі та верхньому одязі.',
      seasons_all: 'Композиція володіє винятковою універсальністю і бездоганно пасуватиме протягом усіх чотирьох сезонів.',

      time_day: 'Прекрасний вибір для щоденного носіння, офісу чи ділових зустрічей.',
      time_night: 'Ідеальний супутник для вечірніх виходів, побачень та особливих подій, коли хочеться запам\'ятатися.',
      time_all: 'Гармонійно звучить як зранку під час робочого дня, так і під час вечірніх подій.',

      sillage_intimate: 'Аромат звучить делікатно та інтимно — лише для найближчих людей.',
      sillage_medium: 'Шлейф помірний та вишуканий: підкреслює вашу присутність без надмірної нав\'язливості.',
      sillage_strong: 'Виразний і гучний шлейф гарантовано звертатиме на вас захоплені погляди та компліменти.',

      labels: {
        gender: 'Стиль',
        seasons: 'Сезон',
        time: 'Час доби',
        sillage: 'Шлейф',
        chords: 'Головні акорди',
        stability: 'Стійкість',
      },
      shareTitle: '✨ Мій ольфакторний профіль у MayParfum',
      shareArchetype: '👑 Архетип',
      shareChords: '🌿 Характер',
      shareSeason: '❄️ Сезон',
      shareSillage: '💫 Шлейф',
      shareDescription: '📝 Опис',
      shareTopPerfumes: '🏆 Рекомендовані аромати',
      shareCta: 'Пройди тест та дізнайся свій ольфакторний профіль:',
    },
  },
  en: {
    archetypes: {
      fresh_woody: 'Noble Urbanist: Freshness & Woods',
      leather_oriental: 'Charismatic Alchemist: Smoke, Leather & Spice',
      floral_fruity: 'Romantic Muse: Radiant Flowers & Fruit',
      sweet_amber: 'Sensual Gourmand: Warm Velvet & Spices',
      musky_fresh: 'Pure Silk: Linen Musk & Fresh Air',
      woody_oriental: 'Oriental Aristocrat: Noble Woods & Amber',
      fresh_citrus: 'Oceanic Drive: Citrus Breeze & Energy',
      woody: 'Woody Monolith: Cedar, Sandalwood & Poise',
      floral: 'Floral Symphony: Delicate Petals & Elegance',
      fruity: 'Sunlit Nectar: Ripe Fruits & Tropical Sun',
      sweet_gourmand: 'Dessert Charm: Exquisite Vanilla & Caramel',
      oriental_spicy: 'Spicy Magnetism: Mysterious Incense & Amber',
      leather_smoky: 'Dark Knight: Vintage Leather & Noble Smoke',
      musky_clean: 'Crystal Musk: Clean Cotton & Powder',
      animalic: 'Primal Passion: Bold Animalic Aura',
      universal: 'Bespoke Olfactory Harmony',
    },
    taglines: {
      fresh_woody: 'Energetic harmony of natural fresh notes anchored by deep cedar and woods.',
      leather_oriental: 'Deep, magnetic, and intense aura with an unmistakable signature.',
      floral_fruity: 'Lighthearted, playful, and charming blend of blooming botanicals.',
      sweet_amber: 'Enveloping, cozy, and alluring profile with velvety richness.',
      musky_fresh: 'Effortless cleanliness and crystal-clear elegance.',
      woody_oriental: 'Rich and prestigious character imbued with warm balsamic resins.',
      fresh_citrus: 'Pure wave of vitality, marine zest, and sparkling citrus.',
      woody: 'Understated, grounding, and confident woody architecture.',
      floral: 'Timeless classic blooming with delicate garden blossoms.',
      fruity: 'Vibrant explosion of succulent fruit, berries, and optimism.',
      sweet_gourmand: 'Delicious, indulgent scent of warm Madagascar vanilla and treats.',
      oriental_spicy: 'Enchanting journey into exotic spices and glowing amber twilight.',
      leather_smoky: 'Bold, daring, and sophisticated profile of rich leather and tobacco.',
      musky_clean: 'Soft powder cloud sitting intimately close to your skin.',
      animalic: 'Provocative, hypnotic, and unapologetic character.',
      universal: 'Balanced notes meticulously composed for any moment.',
    },
    narratives: {
      intro_woody: 'Your olfactory choice exudes poise, confidence, and timeless nobility.',
      intro_fresh: 'Your fragrance portrait radiates vitality, freedom, and an active lifestyle.',
      intro_floral: 'Your selection expresses delicate romance, subtle tenderness, and graceful charm.',
      intro_gourmand: 'Your profile radiates cozy warmth, sweet allure, and mouth-watering luxury.',
      intro_leather: 'Your presence commands attention with rebellious boldness and leather luxury.',
      intro_oriental: 'Your signature wraps you in warm spices, precious resins, and amber mysticism.',
      intro_clean: 'Your style represents spotless minimalism, crisp clean cotton, and serenity.',
      intro_default: 'Your personalized olfactory portrait has been crafted from your selected nuances.',

      seasons_warm: 'This composition shines gloriously in warm weather, sparkling under sunshine.',
      seasons_cold: 'This fragrance unfolds majestically in cooler and frosty days, warming with its rich heart.',
      seasons_heat: 'Formulated for hot summer days: deeply refreshing, uplifting, and comforting.',
      seasons_frost: 'A dense, comforting trail designed for autumn and winter chill.',
      seasons_all: 'Versatile formulation suitable across all four seasons and climate changes.',

      time_day: 'A splendid choice for daily wear, office hours, and casual meetings.',
      time_night: 'The perfect evening companion for memorable nights, dates, and special celebrations.',
      time_all: 'Transitions seamlessly from morning work to captivating evening moments.',

      sillage_intimate: 'Radiates with understated discretion — an intimate pleasure for those closest.',
      sillage_medium: 'Balanced and sophisticated trail: noticeable without being overpowering.',
      sillage_strong: 'Bold and commanding projection that turns heads and garners compliments.',

      labels: {
        gender: 'Style',
        seasons: 'Season',
        time: 'Time of Day',
        sillage: 'Sillage',
        chords: 'Key Accords',
        stability: 'Longevity',
      },
      shareTitle: '✨ My Fragrance Olfactory Profile at MayParfum',
      shareArchetype: '👑 Archetype',
      shareChords: '🌿 Accords',
      shareSeason: '❄️ Season',
      shareSillage: '💫 Sillage',
      shareDescription: '📝 Narrative',
      shareTopPerfumes: '🏆 Recommended Perfumes',
      shareCta: 'Take the quiz and discover your fragrance profile:',
    },
  },
  ru: {
    archetypes: {
      fresh_woody: 'Благородный урбанист: свежесть и дерево',
      leather_oriental: 'Харизматичный алхимик: дым, кожа и специи',
      floral_fruity: 'Романтическая муза: солнечные цветы и фрукты',
      sweet_amber: 'Чувственный гурман: тёплый бархат и пряности',
      musky_fresh: 'Атлас чистоты: шёлковый мускус и свежесть',
      woody_oriental: 'Восточный аристократ: благородный лес и амбра',
      fresh_citrus: 'Океанический драйв: цитрусовый бриз и энергия',
      woody: 'Древесный монолит: выдержка, кедр и сандал',
      floral: 'Цветочная симфония: утончённые лепестки и элегантность',
      fruity: 'Солнечный нектар: спелые фрукты и летнее солнце',
      sweet_gourmand: 'Десертный шарм: изысканная ваниль и сладости',
      oriental_spicy: 'Пряный магнетизм: таинственный восток и ладан',
      leather_smoky: 'Тёмный рыцарь: винтажная кожа и благородный дым',
      musky_clean: 'Хрустальный мускус: прикосновение чистоты и пудры',
      animalic: 'Первозданная страсть: дерзкий анималистичный аккорд',
      universal: 'Индивидуальная ольфакторная гармония',
    },
    taglines: {
      fresh_woody: 'Энергичный баланс природной свежести и благородной древесной основы.',
      leather_oriental: 'Глубокий, магнетический и загадочный шлейф с сильным характером.',
      floral_fruity: 'Лёгкий, жизнерадостный и соблазнительный коктейль женственности и радости.',
      sweet_amber: 'Окутывающий, уютный и притягательный аромат с нежным бархатом.',
      musky_fresh: 'Элегантная лёгкость и ощущение свежевыстиранного белья на солнце.',
      woody_oriental: 'Богатый и респектабельный характер с тёплыми смолистыми оттенками.',
      fresh_citrus: 'Чистый заряд бодрости, морского бриза и искрящихся цитрусов.',
      woody: 'Сдержанный, глубокий и уверенный в себе древесный профиль.',
      floral: 'Утончённая классика, наполненная нежностью весеннего сада.',
      fruity: 'Яркий взрыв тропических фруктов, сочных ягод и оптимизма.',
      sweet_gourmand: 'Аппетитный, тёплый и роскошный аромат растопленной карамели и ванили.',
      oriental_spicy: 'Загадочное путешествие по восточным пряностям и амбровым сумеркам.',
      leather_smoky: 'Смелый, бунтарский и статусный профиль с табаком и кожей.',
      musky_clean: 'Нежное пудровое облачко, интимно держащееся близко к коже.',
      animalic: 'Провокационный, гипнотический и страстный темперамент.',
      universal: 'Сбалансированное сочетание нот для любого события и настроения.',
    },
    narratives: {
      intro_woody: 'Ваш ольфакторный выбор подчёркивает глубокую уверенность, статус и благородство.',
      intro_fresh: 'Ваш ароматный портрет дышит свежестью, движением, свободой и лёгкостью.',
      intro_floral: 'Ваш выбор раскрывает утончённый романтизм, чувствительность и элегантность.',
      intro_gourmand: 'Ваш профиль излучает уют, тепло, сладкую притягательность и шарм.',
      intro_leather: 'Ваш образ полон харизмы, смелости, сдержанной силы и винтажного шика.',
      intro_oriental: 'Ваш аромат окутывает восточной тайной, благородными смолами и пряной страстью.',
      intro_clean: 'Ваш почерк — безупречная чистота, опрятность и изысканный минимализм.',
      intro_default: 'Ваш персональный ольфакторный профиль создан на основе выбранных акцентов.',

      seasons_warm: 'Такая композиция великолепно звучит в тёплое время года, переливаясь под солнечными лучами.',
      seasons_cold: 'Этот аромат идеально раскрывается в прохладную и морозную пору, окутывая благородным теплом.',
      seasons_heat: 'Создан для жарких летних дней: освежает, тонизирует и дарит ощущение комфорта.',
      seasons_frost: 'Густая и плотная база создана для осенне-зимней погоды, оставляя роскошный шлейф.',
      seasons_all: 'Композиция обладает универсальностью и безупречно подойдёт для всех 4 сезонов.',

      time_day: 'Прекрасный выбор для повседневного ношения, офиса или деловых встреч.',
      time_night: 'Идеальный спутник для вечерних выходов, свиданий и особенных событий.',
      time_all: 'Гармонично звучит как утром на работе, так и во время вечерних мероприятий.',

      sillage_intimate: 'Аромат звучит деликатно и интимно — только для самых близких людей.',
      sillage_medium: 'Шлейф умеренный и изысканный: подчёркивает ваше присутствие без навязчивости.',
      sillage_strong: 'Выразительный шлейф гарантированно привлечёт восторженные взгляды и комплименты.',

      labels: {
        gender: 'Стиль',
        seasons: 'Сезон',
        time: 'Время суток',
        sillage: 'Шлейф',
        chords: 'Главные аккорды',
        stability: 'Стойкость',
      },
      shareTitle: '✨ Мой ольфакторный профиль в MayParfum',
      shareArchetype: '👑 Архетип',
      shareChords: '🌿 Характер',
      shareSeason: '❄️ Сезон',
      shareSillage: '💫 Шлейф',
      shareDescription: '📝 Описание',
      shareTopPerfumes: '🏆 Рекомендованные ароматы',
      shareCta: 'Пройди тест и узнай свой ольфакторный профиль:',
    },
  },
}

function getDict(lang: string) {
  if (lang === 'uk' || lang === 'ua') return profileDict.uk
  if (lang === 'ru') return profileDict.ru
  return profileDict.en
}

export function buildFragranceProfile(
  answers: Record<string, string[]>,
  steps: QuizStep[],
  lang: string = 'uk'
): FragranceProfile {
  const d = getDict(lang)
  const chords = answers['chords'] || []
  const seasons = answers['seasons']?.[0] || '__any__'
  const time = answers['time']?.[0] || '__any__'
  const sillage = answers['sillage'] || []

  // 1. Identify Archetype
  let key: keyof typeof d.archetypes = 'universal'
  const has = (val: string) => chords.includes(val)

  if (has('grp_woody') && has('grp_fresh_citrus')) {
    key = 'fresh_woody'
  } else if (has('grp_leather_smoky') && has('grp_oriental_amber_spicy')) {
    key = 'leather_oriental'
  } else if (has('grp_floral') && has('grp_fruity')) {
    key = 'floral_fruity'
  } else if (has('grp_sweet_gourmand') && has('grp_oriental_amber_spicy')) {
    key = 'sweet_amber'
  } else if (has('grp_musky_powdery_clean') && has('grp_fresh_citrus')) {
    key = 'musky_fresh'
  } else if (has('grp_woody') && has('grp_oriental_amber_spicy')) {
    key = 'woody_oriental'
  } else if (has('grp_fresh_citrus')) {
    key = 'fresh_citrus'
  } else if (has('grp_woody')) {
    key = 'woody'
  } else if (has('grp_floral')) {
    key = 'floral'
  } else if (has('grp_fruity')) {
    key = 'fruity'
  } else if (has('grp_sweet_gourmand')) {
    key = 'sweet_gourmand'
  } else if (has('grp_oriental_amber_spicy')) {
    key = 'oriental_spicy'
  } else if (has('grp_leather_smoky')) {
    key = 'leather_smoky'
  } else if (has('grp_musky_powdery_clean')) {
    key = 'musky_clean'
  } else if (has('grp_animalic')) {
    key = 'animalic'
  }

  const archetypeTitle = d.archetypes[key]
  const tagline = d.taglines[key]

  // 2. Build Narrative sentences
  const narrativeParts: string[] = []

  // Intro based on dominant chord
  if (has('grp_woody')) {
    narrativeParts.push(d.narratives.intro_woody)
  } else if (has('grp_fresh_citrus')) {
    narrativeParts.push(d.narratives.intro_fresh)
  } else if (has('grp_floral')) {
    narrativeParts.push(d.narratives.intro_floral)
  } else if (has('grp_sweet_gourmand')) {
    narrativeParts.push(d.narratives.intro_gourmand)
  } else if (has('grp_leather_smoky')) {
    narrativeParts.push(d.narratives.intro_leather)
  } else if (has('grp_oriental_amber_spicy')) {
    narrativeParts.push(d.narratives.intro_oriental)
  } else if (has('grp_musky_powdery_clean')) {
    narrativeParts.push(d.narratives.intro_clean)
  } else {
    narrativeParts.push(d.narratives.intro_default)
  }

  // Seasonality
  if (seasons === 'grp_warm') {
    narrativeParts.push(d.narratives.seasons_warm)
  } else if (seasons === 'grp_cold') {
    narrativeParts.push(d.narratives.seasons_cold)
  } else if (seasons === 'grp_heat') {
    narrativeParts.push(d.narratives.seasons_heat)
  } else if (seasons === 'grp_frost') {
    narrativeParts.push(d.narratives.seasons_frost)
  } else {
    narrativeParts.push(d.narratives.seasons_all)
  }

  // Time of Day
  if (time === 'day') {
    narrativeParts.push(d.narratives.time_day)
  } else if (time === 'night') {
    narrativeParts.push(d.narratives.time_night)
  } else if (time === '__any__' && steps.some((s) => s.id === 'time')) {
    narrativeParts.push(d.narratives.time_all)
  }

  // Sillage
  const isStrongSillage = sillage.some((s) => ['i-strong', 'i-heavy', 'i-enormous', 'i-nuclear'].includes(s))
  const isIntimateSillage = sillage.some((s) => ['i-intimate', 'i-soft'].includes(s))
  if (isStrongSillage) {
    narrativeParts.push(d.narratives.sillage_strong)
  } else if (isIntimateSillage) {
    narrativeParts.push(d.narratives.sillage_intimate)
  } else if (sillage.includes('i-medium')) {
    narrativeParts.push(d.narratives.sillage_medium)
  }

  const narrative = narrativeParts.join(' ')

  // 3. Extract Human-readable Facets
  const facets: FragranceFacet[] = []
  const chordsList: string[] = []

  for (const step of steps) {
    const selectedVals = answers[step.id] ?? []
    if (selectedVals.length === 0 || (selectedVals.length === 1 && selectedVals[0] === '__any__')) {
      continue
    }

    const labels = selectedVals
      .map((val) => step.options.find((opt) => opt.value === val)?.label || val)
      .map((lbl) => lbl.replace(/\s*\([^)]*\)/g, '')) // Remove paren notes for badges

    if (step.id === 'chords') {
      chordsList.push(...labels)
      facets.push({
        label: d.narratives.labels.chords,
        value: labels.join(', '),
        icon: '🌿',
      })
    } else if (step.id === 'seasons') {
      facets.push({
        label: d.narratives.labels.seasons,
        value: labels.join(', '),
        icon: '❄️',
      })
    } else if (step.id === 'gender') {
      facets.push({
        label: d.narratives.labels.gender,
        value: labels.join(', '),
        icon: '👤',
      })
    } else if (step.id === 'sillage') {
      facets.push({
        label: d.narratives.labels.sillage,
        value: labels.join(', '),
        icon: '💫',
      })
    } else if (step.id === 'time') {
      facets.push({
        label: d.narratives.labels.time,
        value: labels.join(', '),
        icon: '🕒',
      })
    } else if (step.id === 'stability') {
      facets.push({
        label: d.narratives.labels.stability,
        value: labels.join(', '),
        icon: '⏳',
      })
    }
  }

  return {
    archetypeTitle,
    tagline,
    narrative,
    facets,
    chordsList,
    recommendedVibe: tagline,
  }
}

/**
 * Encodes answers object to a compact, URL-safe Base64 string
 */
export function encodeQuizState(answers: Record<string, string[]>): string {
  try {
    const clean: Record<string, string[]> = {}
    for (const [k, v] of Object.entries(answers)) {
      if (Array.isArray(v) && v.length > 0) {
        clean[k] = v
      }
    }
    const jsonStr = JSON.stringify(clean)
    // UTF-8 safe base64
    const base64 = btoa(encodeURIComponent(jsonStr).replace(/%([0-9A-F]{2})/g, (_, p1) => 
      String.fromCharCode(parseInt(p1, 16))
    ))
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  } catch {
    return ''
  }
}

/**
 * Decodes URL-safe Base64 string back into answers object
 */
export function decodeQuizState(stateStr: string): Record<string, string[]> | null {
  if (!stateStr) return null
  try {
    let base64 = stateStr.replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) {
      base64 += '='
    }
    const jsonStr = decodeURIComponent(
      Array.prototype.map
        .call(atob(base64), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const parsed = JSON.parse(jsonStr)
    if (parsed && typeof parsed === 'object') {
      return parsed
    }
  } catch {
    // Fallback: try raw JSON if passed directly
    try {
      const direct = JSON.parse(decodeURIComponent(stateStr))
      if (direct && typeof direct === 'object') return direct
    } catch {
      return null
    }
  }
  return null
}

/**
 * Builds formatted text for copying to clipboard or sharing via Telegram
 */
export function buildShareText(
  profile: FragranceProfile,
  products: QuizMatchProduct[],
  shareUrl: string,
  lang: string = 'uk'
): string {
  const d = getDict(lang)
  const lines: string[] = []

  lines.push(d.narratives.shareTitle)
  lines.push('')
  lines.push(`${d.narratives.shareArchetype}: «${profile.archetypeTitle}»`)
  lines.push(`✨ ${profile.tagline}`)
  lines.push('')

  for (const facet of profile.facets) {
    lines.push(`${facet.icon || '▫️'} ${facet.label}: ${facet.value}`)
  }
  lines.push('')

  lines.push(`${d.narratives.shareDescription}:`)
  lines.push(profile.narrative)
  lines.push('')

  if (products.length > 0) {
    lines.push(`${d.narratives.shareTopPerfumes}:`)
    products.slice(0, 3).forEach((p, idx) => {
      const name = lang === 'uk' ? p.name_uk || p.name_en : p.name_en || p.name_uk
      lines.push(`${idx + 1}. ${name}`)
    })
    lines.push('')
  }

  lines.push(d.narratives.shareCta)
  lines.push(shareUrl)

  return lines.join('\n')
}
